<template>
  <section id="reviews" :class="['reviews', { 'reviews--no-reviews': hasNoReviews }]">
    <div class="reviews__header">
      <h3 class="reviews__headline">{{ $t('headline', { product: product.name }) }}</h3>
      <div class="reviews__average">
        <FiveStarsRating
          :value="reviewRatingAverageOverall"
          :variant="FIVE_STAR_RATING_VARIANTS.large"
          v-data-test-id="'review-average'"
        />
        <div
          :class="['reviews__average-hint', { 'reviews__average-hint--no-reviews': hasNoReviews }]"
        >
          {{ $tc('rating_average_hint', reviewTotalOverall, { count: reviewTotalOverall }) }}
        </div>
      </div>
      <div class="reviews__cta-wrap--header">
        <Button
          v-show="!isQuestionsTabActive && !hasAddReviewCtaHidden"
          ref="addReviewCta"
          class="cta-button reviews__add-review-cta"
          :isInactive="hasSubmittedReview"
          :color="BUTTON_STYLES.color.black"
          type="button"
          v-data-test-id="'add-review-cta'"
          @click="onAddReviewClick"
        >
          {{ $t('leave_review') }}
        </Button>
        <Button
          v-show="isQuestionsTabActive && IS_ASK_QUESTIONS_CTA_VISIBLE"
          ref="addQuestionCta"
          :color="BUTTON_STYLES.color.black"
          type="button"
          class="cta-button reviews__add-question-cta"
          v-data-test-id="'add-question-cta'"
          @click="onAskQuestionClick"
        >
          {{ $t('ask_question') }}
        </Button>
      </div>
    </div>
    <TabControl ref="ReviewsTabControl" :activeTabId.sync="activeTabId" class="reviews__inner">
      <TabItem :id="TAB_IDS.reviews" :name="this.$t('reviews')">
        <LoadingIndicator v-if="isLoadingFirstReviews" />
        <div v-else-if="reviewTotalOverall > 0" class="reviews__wrapper" v-data-test-id="'reviews'">
          <div class="reviews__sorting-container">
            <span class="reviews__aggregate">{{ reviewTotalOverall + ' ' + $t('reviews') }}</span>
            <Sorting
              :sortOptions="sortOptions"
              :activeOption="activeSort"
              class="reviews__sorting"
              @selected="onSortingOptionsSelected"
            />
          </div>
          <transition-group name="slide-and-appear" tag="div" class="reviews__blocks">
            <div
              v-for="review in reviews"
              :key="review.id"
              ref="reviewBlock"
              class="reviews__block"
              v-data-test-id="'review-block'"
            >
              <SeoReviewRating
                :productName="product.name"
                :productDescription="product.description"
                :author="review.author"
                :ratingValue="review.rating"
                :reviewBody="review.review"
              />
              <div class="reviews__block-main-wrapper">
                <div class="reviews__details">
                  <div class="reviews__details-rating">
                    <FiveStarsRating
                      :value="review.rating"
                      :variant="FIVE_STAR_RATING_VARIANTS.small"
                      v-data-test-id="'review-item'"
                    />
                  </div>
                  <div class="reviews__details-row" v-show="review.sizeFit">
                    <span class="reviews__details-label">{{ $t('size_fit_label') }}:</span>
                    <span class="reviews__details-data">
                      {{ $t(`size_fit.${review.sizeFit}`) }}
                    </span>
                  </div>
                  <div class="reviews__details-row" v-show="review.runningReason">
                    <span class="reviews__details-label">{{ $t('activity_label') }}:</span>
                    <span class="reviews__details-data">
                      {{ $t(`activity.${review.runningReason}`) }}
                    </span>
                  </div>
                  <div class="reviews__details-row" v-show="review.runningDistance">
                    <span class="reviews__details-label">{{ $t('typical_distance_label') }}:</span>
                    <span class="reviews__details-data" v-data-test-id="'review-item-distance'">
                      {{ $t(`typical_distance.${review.runningDistance}.${distanceSystem}`) }}
                    </span>
                  </div>
                  <div class="reviews__details-row" :title="renderTitleizedString(review.location)">
                    <span class="reviews__details-label">{{ $t('location') }}:</span>
                    <span class="reviews__details-data" v-data-test-id="'review-location'">
                      {{ renderTitleizedString(review.location) }}
                    </span>
                  </div>

                  <div class="reviews__details-row" v-show="review.height">
                    <span class="reviews__details-label">{{ $t('height') }}:</span>
                    <span class="reviews__details-data">
                      {{ review.height }}
                    </span>
                  </div>
                  <div class="reviews__details-row" v-show="review.weight">
                    <span class="reviews__details-label">{{ $t('weight') }}:</span>
                    <span class="reviews__details-data">
                      {{ review.weight }}
                    </span>
                  </div>
                  <div class="reviews__details-row" v-show="review.product && review.product.color">
                    <span class="reviews__details-label">{{ $t('color') }}:</span>
                    <span class="reviews__details-data">
                      {{ review.product ? review.product.color : '' }}
                    </span>
                  </div>
                </div>
                <div class="reviews__copy-wrap">
                  <h4 class="reviews__title">{{ renderTitleizedString(review.title) }}</h4>
                  <FormattedText
                    class="reviews__copy"
                    :text="review.review"
                    :visibleLineCount="isMqMedium ? 5 : 4"
                    :options="formattedTextOptions"
                  />
                  <div class="reviews__author">{{ renderTitleizedString(review.author) }}</div>
                </div>
              </div>
              <div class="reviews__comment-wrap" v-if="review.comment">
                <h4 class="reviews__title--response">Response from On</h4>
                <FormattedText
                  class="reviews__copy"
                  :text="review.comment.content"
                  :visibleLineCount="isMqMedium ? 5 : 4"
                  :options="formattedTextOptions"
                />
              </div>
            </div>
          </transition-group>

          <div class="reviews__cta-wrap">
            <Button
              v-if="hasMoreReviewsToLoad"
              :color="BUTTON_STYLES.color.white"
              :isSubmitting="isLoadingMoreReviews"
              type="button"
              class="reviews__load-cta"
              v-data-test-id="'review-load-more-cta'"
              @click="onLoadMoreReviewsClick"
              >{{ $t('load_more') }}</Button
            >
          </div>
        </div>
        <div v-else>
          <div class="reviews__no-reviews">
            <h4 class="reviews__subheadline">{{ $t('no_reviews_available') }}</h4>
          </div>
        </div>
      </TabItem>
      <TabItem :id="TAB_IDS.questions" :name="this.$t('questions')">
        <Questions v-data-test-id="'questions'" />
      </TabItem>
    </TabControl>
  </section>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { GTM_EVENT_CATEGORIES } from '@/services/external-communication/gtm/gtm-events'
import { observeIntersection } from '@/helpers/intersection-observer'
import { scrollSmoothlyToElement } from '@/helpers/scroll'
import { titleize, isEmpty } from '@/helpers/string'
import FiveStarsRating, {
  VARIANTS as FIVE_STAR_RATING_VARIANTS,
} from '@/components/_shared/Form/FiveStarsRating/FiveStarsRating.vue'
import FormattedText from '@/components/_shared/FormattedText.vue'
import Button, { BUTTON_STYLES } from '@/components/_shared/Button.vue'
import LoadingIndicator from '@/components/_shared/LoadingIndicator.vue'
import TabControl from '@/components/_shared/TabControl/TabControl.vue'
import TabItem from '@/components/_shared/TabControl/Tabitem.vue'
import SeoReviewRating from './SeoReviewRating.vue'
import Questions from './Questions.vue'
import Sorting from '@/components/_shared/Sorting/Sorting.vue'

const SLIDE_AND_APPEAR_TRANSLATION = 90

export const TAB_IDS = {
  reviews: 'reviews',
  questions: 'questions',
}

export default {
  name: 'Reviews',
  components: {
    SeoReviewRating,
    FiveStarsRating,
    FormattedText,
    Button,
    LoadingIndicator,
    TabControl,
    TabItem,
    Questions,
    Sorting,
  },

  data() {
    return {
      hasSubmittedReview: false,
      activeTabId: TAB_IDS.reviews,
      BUTTON_STYLES,
      FIVE_STAR_RATING_VARIANTS,
      TAB_IDS,
      IS_ASK_QUESTIONS_CTA_VISIBLE: false,
    }
  },

  computed: {
    ...mapState('product', ['reviews', 'sortOptions', 'activeSort']),
    ...mapGetters({
      product: 'product/product',
      isLoadingFirstReviews: 'product/isLoadingFirstReviews',
      isLoadingMoreReviews: 'product/isLoadingMoreReviews',
      hasMoreReviewsToLoad: 'product/hasMoreReviewsToLoad',
      firstReviewIndexOfPageLastAdded: 'product/firstReviewIndexOfPageLastAdded',
      reviewTotalOverall: 'product/reviewTotalOverall',
      reviewRatingAverageOverall: 'product/reviewRatingAverageOverall',
      isMqTiny: 'viewport/isMqTiny',
      isMqMedium: 'viewport/isMqMedium',
      distanceSystem: 'page/distanceSystem',
    }),

    hasNoReviews() {
      return !this.isLoadingFirstReviews && this.reviewTotalOverall === 0
    },

    hasAddReviewCtaHidden() {
      return this.hasSubmittedReview && !this.hasNoReviews
    },

    formattedTextOptions() {
      return this.isMqTiny ? { maxLengthOfConcat: 10 } : {}
    },

    isQuestionsTabActive() {
      return this.activeTabId === TAB_IDS.questions
    },
  },

  mounted() {
    observeIntersection(this.$el, () => {
      this.loadReviews()
      this.preloadLeaveReviewOverlay()
    })
  },

  methods: {
    ...mapActions({
      loadReviews: 'product/loadReviews',
      loadMoreReviews: 'product/loadMoreReviews',
      setSorting: 'product/setSorting',
      preloadLeaveReviewOverlay: 'overlay/preloadLeaveReviewOverlay',
      showLeaveReviewOverlay: 'overlay/showLeaveReviewOverlay',
      showQuestionOverlay: 'overlay/showQuestionOverlay',
    }),

    renderTitleizedString(string) {
      return !isEmpty(string) ? titleize(string) : '-'
    },

    async onLoadMoreReviewsClick(event) {
      event.target.blur()
      await this.loadMoreReviews()
      await this.$nextTick()
      scrollSmoothlyToElement(this.$refs.reviewBlock[this.firstReviewIndexOfPageLastAdded], {
        duration: On.TransitionTimes.slower,
        offset: -SLIDE_AND_APPEAR_TRANSLATION,
      })
    },

    onAddReviewClick() {
      this.hasSubmittedReview && this.hasNoReviews
        ? this.$refs.addReviewCta.shake()
        : this.showLeaveReviewOverlay({ product: this.product, onSuccess: this.onReviewSubmit })
      this.$notifier.trackEvent({
        action: this.$notifier.ACTIONS.productInteraction,
        details: { action: 'review', label: 'add' },
      })
    },

    onAskQuestionClick() {
      this.hasSubmittedQuestion
        ? this.$refs.addQuestionCta.shake()
        : this.showQuestionOverlay({ onSuccess: this.onQuestionSubmit })
      this.$notifier.trackEvent({
        action: this.$notifier.ACTIONS.productInteraction,
        details: { action: 'question', label: 'add' },
      })
    },

    onReviewSubmit() {
      this.hasSubmittedReview = true
      this.$notifier.trackEvent({
        action: this.$notifier.ACTIONS.productInteraction,
        details: { action: 'review', label: 'submit' },
      })
    },

    onQuestionSubmit() {
      this.hasSubmittedQuestion = true
      this.$notifier.trackEvent({
        action: this.$notifier.ACTIONS.productInteraction,
        details: { action: 'question', label: 'submit' },
      })
    },

    onSortingOptionsSelected(option) {
      this.setSorting(option)
      this.$notifier.trackEvent({
        action: this.$notifier.ACTIONS.filterAndSorting,
        details: {
          category: GTM_EVENT_CATEGORIES.productReviews,
          action: 'sort',
          label: option.code,
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import './Reviews';
</style>

<i18n src="@/locales/reviews.json">
</i18n>
